/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface Pipe {
  /**
   * Creates a function that is restricted to invoking passed function once.
   *
   * @public
   * @note this function is not curried, if you want to curry it, then pair it with `f1.fnX`
   * @see f1.funnel for the async version
   * @function f1.pipe
   * @param fns a list of functions which is passed the return value of the next
   * @example
   *
   * ```ts
   * f1.pipe([
   *   f1.to.number,
   *   f1.map(f1.add(3))
   * ]), ['1', '3', 4]) => [4, 6, 7]
   * ```
   */
  <A, B>(fns: [(a: A) => B], seq: A): B;
  <A, B, C>(fns: [(a: A) => B, (b: B) => C], seq: A): C;
  <A, B, C, D>(fns: [(a: A) => B, (b: B) => C, (c: C) => D], seq: A): D;
  <A, B, C, D, E>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E], seq: A): E;
  <A, B, C, D, E, F>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F], seq: A): F;
  <A, B, C, D, E, F, G>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G], seq: A): G;
  <A, B, C, D, E, F, G, H>(
    fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H],
    seq: A
  ): H;
  <A, B, C, D, E, F, G, H, I>(
    fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H, (h: H) => I],
    seq: A
  ): I;
  <A, B, C, D, E, F, G, H, I, J>(
    fns: [
      (a: A) => B,
      (b: B) => C,
      (c: C) => D,
      (d: D) => E,
      (e: E) => F,
      (f: F) => G,
      (g: G) => H,
      (h: H) => I,
      (i: I) => J
    ],
    seq: A
  ): J;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: [
      (a: A) => B,
      (b: B) => C,
      (c: C) => D,
      (d: D) => E,
      (e: E) => F,
      (f: F) => G,
      (g: G) => H,
      (h: H) => I,
      (i: I) => J,
      (j: J) => K
    ],
    seq: A
  ): K;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: ((val: A | B | C | D | E | F | G | H | I | J | K) => A | B | C | D | E | F | G | H | I | J | K)[],
    seq: A | B | C | D | E | F | G | H | I | J | K
  ): A | B | C | D | E | F | G | H | I | J | K;

  <A, B>(fns: [(a: A) => B]): (seq: A) => B;
  <A, B, C>(fns: [(a: A) => B, (b: B) => C]): (seq: A) => C;
  <A, B, C, D>(fns: [(a: A) => B, (b: B) => C, (c: C) => D]): (seq: A) => D;
  <A, B, C, D, E>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E]): (seq: A) => E;
  <A, B, C, D, E, F>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F]): (seq: A) => F;
  <A, B, C, D, E, F, G>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G]): (
    seq: A
  ) => G;
  <A, B, C, D, E, F, G, H>(
    fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H]
  ): (seq: A) => H;
  <A, B, C, D, E, F, G, H, I>(
    fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H, (h: H) => I]
  ): (seq: A) => I;
  <A, B, C, D, E, F, G, H, I, J>(
    fns: [
      (a: A) => B,
      (b: B) => C,
      (c: C) => D,
      (d: D) => E,
      (e: E) => F,
      (f: F) => G,
      (g: G) => H,
      (h: H) => I,
      (i: I) => J
    ]
  ): (seq: A) => J;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: [
      (a: A) => B,
      (b: B) => C,
      (c: C) => D,
      (d: D) => E,
      (e: E) => F,
      (f: F) => G,
      (g: G) => H,
      (h: H) => I,
      (i: I) => J,
      (j: J) => K
    ]
  ): (seq: A) => K;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fns: ((val: A | B | C | D | E | F | G | H | I | J | K) => A | B | C | D | E | F | G | H | I | J | K)[]
  ): (seq: A | B | C | D | E | F | G | H | I | J | K) => A | B | C | D | E | F | G | H | I | J | K;
}

/**
 * @see Pipe
 */
function pipe<A, B>(fns: [(a: A) => B], seq: A): B;
function pipe<A, B, C>(fns: [(a: A) => B, (b: B) => C], seq: A): C;
function pipe<A, B, C, D>(fns: [(a: A) => B, (b: B) => C, (c: C) => D], seq: A): D;
function pipe<A, B, C, D, E>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E], seq: A): E;
function pipe<A, B, C, D, E, F>(fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F], seq: A): F;
function pipe<A, B, C, D, E, F, G>(
  fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G],
  seq: A
): G;
function pipe<A, B, C, D, E, F, G, H>(
  fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H],
  seq: A
): H;
function pipe<A, B, C, D, E, F, G, H, I>(
  fns: [(a: A) => B, (b: B) => C, (c: C) => D, (d: D) => E, (e: E) => F, (f: F) => G, (g: G) => H, (h: H) => I],
  seq: A
): I;
function pipe<A, B, C, D, E, F, G, H, I, J>(
  fns: [
    (a: A) => B,
    (b: B) => C,
    (c: C) => D,
    (d: D) => E,
    (e: E) => F,
    (f: F) => G,
    (g: G) => H,
    (h: H) => I,
    (i: I) => J
  ],
  seq: A
): J;
function pipe<A, B, C, D, E, F, G, H, I, J, K>(
  fns: [
    (a: A) => B,
    (b: B) => C,
    (c: C) => D,
    (d: D) => E,
    (e: E) => F,
    (f: F) => G,
    (g: G) => H,
    (h: H) => I,
    (i: I) => J,
    (j: J) => K
  ],
  seq: A
): K;
function pipe<A, B, C, D, E, F, G, H, I, J, K>(
  fns: ((val: A | B | C | D | E | F | G | H | I | J | K) => A | B | C | D | E | F | G | H | I | J | K)[],
  seq: A | B | C | D | E | F | G | H | I | J | K
): A | B | C | D | E | F | G | H | I | J | K {
  const n = fns.length;
  let i = 0;
  for (; i < n; i += 1) {
    seq = fns[i](seq);
  }

  return seq;
}

export default pipe;
